import React, { useState } from 'react'
import { Upload, FileSignature } from 'lucide-react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Button, Input, Label,
} from '../ui'
import { uploadPlayerDocument, registrarConsentimientoFisico, renderLegalDoc, LEGAL_DOC_KEYS } from '../../lib/supabase'
import { useOrganization } from '../../contexts/OrganizationContext'
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard'
import LegalDocLink from '../legal/LegalDocLink'

const PARENTESCOS = ['Madre', 'Padre', 'Tutor legal', 'Abuelo o abuela', 'Otro']

// Vía física del consentimiento: el tutor firmó en papel y el club sube el PDF.
//
// No es un atajo ni un caso raro. Quien no tiene teléfono ni correo no puede recibir
// un código, y sin esta salida el OTP no sería más estricto: sería excluyente. En
// RAIDERS son 58 de 207 jugadores.
//
// El documento es obligatorio: una firma de papel sin el papel no prueba nada. Por
// eso el diálogo sube el archivo y registra el consentimiento en un solo paso -- si
// fueran dos, quedaría gente con el consentimiento marcado y sin documento detrás.
const ConsentimientoFisicoDialog = ({ open, onOpenChange, jugadorId, jugadorNombre, onDone }) => {
  const { org } = useOrganization()
  const [nombre, setNombre] = useState('')
  const [parentesco, setParentesco] = useState('')
  const [imagen, setImagen] = useState(false)
  const [file, setFile] = useState(null)
  const [cartaVigente, setCartaVigente] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const guard = useUnsavedGuard({ onClose: () => onOpenChange(false) })

  const carta = renderLegalDoc(LEGAL_DOC_KEYS.CARTA_CONSENTIMIENTO, {
    '[NOMBRE DEL CLUB]': org?.name,
  })

  const puedeGuardar = nombre.trim() && parentesco && file && !saving

  const limpiar = () => {
    setNombre(''); setParentesco(''); setImagen(false); setFile(null); setCartaVigente(false); setError(null)
    guard.resetDirty()
  }

  const handleGuardar = async () => {
    if (!puedeGuardar) return
    setSaving(true)
    setError(null)

    const { data: doc, error: upErr } = await uploadPlayerDocument(jugadorId, {
      scope: 'club',
      docType: 'consentimiento',
      file,
      notes: `Consentimiento firmado por ${nombre.trim()}`,
    })
    if (upErr || !doc?.id) {
      setSaving(false)
      setError(upErr?.message || 'No se pudo subir el documento.')
      return
    }

    const { error: regErr } = await registrarConsentimientoFisico([jugadorId], {
      documentoId: doc.id,
      consienteNombre: nombre.trim(),
      consienteParentesco: parentesco,
      // Va dentro del bloque obligatorio de la carta, igual que en la vía digital.
      autorizaMyklubb: true,
      usaImagen: imagen,
      nota: `Firma en papel recibida por ${org?.name || 'el club'}`,
      // La version que se firmo de verdad, no la que esta pantalla ofrece hoy: una carta
      // impresa antes de la 1.1 y subida despues no cubre torneos. Sin marcar, 1.0.
      versionCarta: cartaVigente && carta?.version ? carta.version : '1.0',
    })
    setSaving(false)
    if (regErr) { setError(regErr.message); return }

    limpiar()
    onOpenChange(false)
    onDone?.()
  }

  return (
    <Dialog open={open} onOpenChange={guard.onOpenChange}>
      <DialogContent {...guard.dirtyProps} className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Registrar consentimiento firmado en papel</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Para {jugadorNombre}. Imprime la carta, recábala firmada y súbela aquí. Queda
            registrada con la fecha y con tu nombre como quien la verificó.
          </p>

          {carta && (
            <LegalDocLink
              doc={carta}
              label="Ver la carta para imprimir →"
              className="text-sm text-primary underline hover:opacity-80"
            />
          )}

          <div className="space-y-2">
            <Label htmlFor="cf_nombre">
              Nombre de quien firmó <span className="text-destructive">*</span>
            </Label>
            <Input
              id="cf_nombre"
              value={nombre}
              onChange={(e) => { guard.markDirty(); setNombre(e.target.value) }}
              placeholder="Como aparece en la firma"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cf_parentesco">
              Parentesco <span className="text-destructive">*</span>
            </Label>
            <select
              id="cf_parentesco"
              value={parentesco}
              onChange={(e) => { guard.markDirty(); setParentesco(e.target.value) }}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">Seleccionar…</option>
              {PARENTESCOS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="cf_file">
              Documento firmado <span className="text-destructive">*</span>
            </Label>
            <Input
              id="cf_file"
              type="file"
              accept=".pdf,image/*"
              onChange={(e) => { guard.markDirty(); setFile(e.target.files?.[0] || null) }}
            />
            <p className="text-xs text-muted-foreground">
              PDF o foto de la carta firmada. Sin el documento no se puede registrar.
            </p>
          </div>

          <label className="flex items-start gap-3 p-3 rounded-md border cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 mt-0.5 rounded border-input"
              checked={imagen}
              onChange={(e) => { guard.markDirty(); setImagen(e.target.checked) }}
            />
            <span className="text-sm text-muted-foreground">
              En la carta marcó <span className="text-foreground">sí autorizo</span> el uso de
              imagen para difusión.
            </span>
          </label>

          {carta?.version && (
            <div className="space-y-1">
              <label className="flex items-start gap-3 p-3 rounded-md border cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 mt-0.5 rounded border-input"
                  checked={cartaVigente}
                  onChange={(e) => { guard.markDirty(); setCartaVigente(e.target.checked) }}
                />
                <span className="text-sm text-foreground">
                  La carta firmada trae el punto «Autorizo que, si el Club inscribe al menor en un
                  torneo o liga, comparta con la organizadora su nombre, número, categoría, fecha de
                  nacimiento, CURP y fotografía…»
                </span>
              </label>
              <p className="text-xs text-muted-foreground">
                Marca la casilla solo si la carta que subes es la versión vigente ({carta.version}).
                Una carta impresa antes no cubre torneos: el jugador juega, pero su roster en el
                torneo mostrará «Falta firma del tutor».
              </p>
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => guard.onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={!puedeGuardar}>
            {saving ? (
              <><Upload className="h-4 w-4 mr-1.5 animate-pulse" /> Guardando…</>
            ) : (
              <><FileSignature className="h-4 w-4 mr-1.5" /> Registrar consentimiento</>
            )}
          </Button>
        </DialogFooter>
        {guard.DiscardDialog}
      </DialogContent>
    </Dialog>
  )
}

export default ConsentimientoFisicoDialog
